/**
 * Spreadsheet export without third-party code: CSV (RFC 4180), a minimal Office Open
 * XML workbook (.xlsx) and an uncompressed ZIP container. Pure functions, so every
 * platform produces identical files and they are unit-tested byte for byte.
 */

export type Cell = string | number | null;
export interface Sheet { name: string; header: string[]; rows: Cell[][] }

const encoder = new TextEncoder();

/**
 * CSV with CRLF line ends and a UTF-8 BOM (Excel otherwise misreads non-ASCII).
 * Text that a spreadsheet would run as a formula (= + - @, tab, CR) is prefixed with
 * an apostrophe; numbers are written as numbers.
 */
export function toCsv(sheet: Sheet): string {
  const field = (value: Cell): string => {
    if (value === null) return '';
    if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
    const text = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  return '﻿' + [sheet.header, ...sheet.rows].map(r => r.map(field).join(',')).join('\r\n') + '\r\n';
}

/** Removes characters XML 1.0 cannot carry and escapes markup. */
function xmlText(value: string): string {
  // eslint-disable-next-line no-control-regex
  return value.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g, '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export function columnName(index: number): string {
  let name = '';
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) name = String.fromCharCode(65 + ((n - 1) % 26)) + name;
  return name;
}

/** Excel sheet names: ≤31 chars, none of []:*?/\, unique case-insensitively. */
export function sheetNames(names: readonly string[]): string[] {
  const used = new Set<string>();
  return names.map(raw => {
    const base = (raw.replace(/[[\]:*?/\\]/g, ' ').trim() || 'Sheet').slice(0, 31);
    let name = base;
    for (let n = 2; used.has(name.toLowerCase()); n++) name = `${base.slice(0, 31 - String(n).length - 1)} ${n}`;
    used.add(name.toLowerCase());
    return name;
  });
}

function worksheetXml(sheet: Sheet): string {
  const row = (cells: Cell[], r: number, style: string) => `<row r="${r}">` + cells.map((value, c) => {
    const ref = `${columnName(c)}${r}`;
    if (value === null || value === '') return '';
    if (typeof value === 'number') return Number.isFinite(value) ? `<c r="${ref}"${style}><v>${value}</v></c>` : '';
    return `<c r="${ref}" t="inlineStr"${style}><is><t xml:space="preserve">${xmlText(value)}</t></is></c>`;
  }).join('') + '</row>';
  const widths = sheet.header.map((h, c) => Math.min(60, Math.max(h.length, ...sheet.rows.slice(0, 500).map(r => String(r[c] ?? '').length)) + 2));
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
    + '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
    + '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>'
    + '<cols>' + widths.map((w, c) => `<col min="${c + 1}" max="${c + 1}" width="${w}" customWidth="1"/>`).join('') + '</cols>'
    + '<sheetData>' + row(sheet.header, 1, ' s="1"') + sheet.rows.map((r, i) => row(r, i + 2, '')).join('') + '</sheetData>'
    + '</worksheet>';
}

/** A workbook with one worksheet per sheet, as ZIP entries. */
export function xlsxEntries(sheets: readonly Sheet[]): ZipEntry[] {
  const names = sheetNames(sheets.map(s => s.name));
  const head = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
  const files: [string, string][] = [
    ['[Content_Types].xml', head + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
      + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
      + '<Default Extension="xml" ContentType="application/xml"/>'
      + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
      + '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'
      + names.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')
      + '</Types>'],
    ['_rels/.rels', head + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
      + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>'],
    ['xl/workbook.xml', head + '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>'
      + names.map((n, i) => `<sheet name="${xmlText(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('') + '</sheets></workbook>'],
    ['xl/_rels/workbook.xml.rels', head + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
      + names.map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('')
      + `<Relationship Id="rId${names.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`],
    ['xl/styles.xml', head + '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
      + '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>'
      + '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>'
      + '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>'
      + '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>'
      + '<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs>'
      + '</styleSheet>'],
    ...sheets.map((s, i): [string, string] => [`xl/worksheets/sheet${i + 1}.xml`, worksheetXml(s)]),
  ];
  return files.map(([name, text]) => ({ name, data: encoder.encode(text) }));
}

export function toXlsx(sheets: readonly Sheet[], modified = new Date()): Uint8Array<ArrayBuffer> {
  return zip(xlsxEntries(sheets), modified);
}

export interface ZipEntry { name: string; data: Uint8Array }

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

export function crc32(data: Uint8Array): number {
  let c = 0xFFFFFFFF;
  for (let i = 0; i < data.length; i++) c = CRC_TABLE[(c ^ data[i]!) & 0xFF]! ^ (c >>> 8);
  return (c ^ 0xFFFFFFFF) >>> 0;
}

/** Stored (uncompressed) ZIP with UTF-8 names. Entries must stay under 4 GiB in total. */
export function zip(entries: readonly ZipEntry[], modified = new Date()): Uint8Array<ArrayBuffer> {
  const time = (modified.getHours() << 11) | (modified.getMinutes() << 5) | Math.floor(modified.getSeconds() / 2);
  const date = ((Math.max(1980, modified.getFullYear()) - 1980) << 9) | ((modified.getMonth() + 1) << 5) | modified.getDate();
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;
  for (const entry of entries) {
    const name = encoder.encode(entry.name);
    const crc = crc32(entry.data);
    const local = new Uint8Array(30 + name.length);
    const l = new DataView(local.buffer);
    l.setUint32(0, 0x04034b50, true); l.setUint16(4, 20, true); l.setUint16(6, 0x0800, true); l.setUint16(8, 0, true);
    l.setUint16(10, time, true); l.setUint16(12, date, true); l.setUint32(14, crc, true);
    l.setUint32(18, entry.data.length, true); l.setUint32(22, entry.data.length, true); l.setUint16(26, name.length, true);
    local.set(name, 30);
    const central = new Uint8Array(46 + name.length);
    const c = new DataView(central.buffer);
    c.setUint32(0, 0x02014b50, true); c.setUint16(4, 20, true); c.setUint16(6, 20, true); c.setUint16(8, 0x0800, true);
    c.setUint16(12, time, true); c.setUint16(14, date, true); c.setUint32(16, crc, true);
    c.setUint32(20, entry.data.length, true); c.setUint32(24, entry.data.length, true); c.setUint16(28, name.length, true);
    c.setUint32(42, offset, true);
    central.set(name, 46);
    locals.push(local, entry.data);
    centrals.push(central);
    offset += local.length + entry.data.length;
  }
  const centralSize = centrals.reduce((n, b) => n + b.length, 0);
  const end = new Uint8Array(22);
  const e = new DataView(end.buffer);
  e.setUint32(0, 0x06054b50, true); e.setUint16(8, entries.length, true); e.setUint16(10, entries.length, true);
  e.setUint32(12, centralSize, true); e.setUint32(16, offset, true);
  const out = new Uint8Array(offset + centralSize + 22);
  let at = 0;
  for (const part of [...locals, ...centrals, end]) { out.set(part, at); at += part.length; }
  return out;
}

/** File-system-safe name part (keeps letters in any script, digits, space, - _ . ( ) &). */
export function safeFileName(name: string): string {
  return name.normalize('NFC').replace(/[^\p{L}\p{N} \-_.()&']/gu, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) || 'Export';
}

/** Distinct file names within one ZIP. */
export function uniqueNames(names: readonly string[], extension: string): string[] {
  const used = new Set<string>();
  return names.map(raw => {
    const base = safeFileName(raw);
    let name = `${base}${extension}`;
    for (let n = 2; used.has(name.toLowerCase()); n++) name = `${base} (${n})${extension}`;
    used.add(name.toLowerCase());
    return name;
  });
}

export const encodeText = (text: string): Uint8Array<ArrayBuffer> => encoder.encode(text);
